import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import type { CallScoreItem } from '../../../api/generated';
import { getScoreColor } from '../../../utils/scoringRubric';

interface MyScoresTableProps {
  calls: CallScoreItem[];
}

function ScoreCell({ score, max = 10 }: { score: number | null; max?: number }) {
  if (score == null) {
    return (
      <Typography variant="body2" sx={{
        color: "text.secondary"
      }}>—
              </Typography>
    );
  }
  const color = max === 100 ? (score >= 70 ? 'success' : score >= 40 ? 'warning' : 'error') : getScoreColor(score);
  return (
    <Typography variant="body2" color={`${color}.main`} sx={{
      fontWeight: 600
    }}>
      {score}
    </Typography>
  );
}

export function MyScoresTable({ calls }: MyScoresTableProps) {
  const navigate = useNavigate();

  if (calls.length === 0) {
    return (
      <Typography
        sx={{
          color: "text.secondary",
          py: 4,
          textAlign: 'center'
        }}>No scored calls yet
              </Typography>
    );
  }

  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Call</TableCell>
            <TableCell align="center">Overall</TableCell>
            <TableCell align="center">Engage</TableCell>
            <TableCell align="center">Discover</TableCell>
            <TableCell align="center">Present</TableCell>
            <TableCell align="center">Close</TableCell>
            <TableCell>Date</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {calls.map((call) => (
            <TableRow
              key={call.conversation_id}
              hover
              sx={{ cursor: 'pointer' }}
              onClick={() => navigate(`/chat/${call.conversation_id}`)}
            >
              <TableCell>
                <Box>
                  <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                    {call.title || call.prospect_name || 'Untitled'}
                  </Typography>
                  {call.prospect_company && (
                    <Typography variant="caption" sx={{
                      color: "text.secondary"
                    }}>
                      {call.prospect_company}
                    </Typography>
                  )}
                </Box>
              </TableCell>
              <TableCell align="center">
                <ScoreCell score={call.overall_score} max={100} />
              </TableCell>
              <TableCell align="center">
                <ScoreCell score={call.engagement_score} />
              </TableCell>
              <TableCell align="center">
                <ScoreCell score={call.discovery_score} />
              </TableCell>
              <TableCell align="center">
                <ScoreCell score={call.presentation_score} />
              </TableCell>
              <TableCell align="center">
                <ScoreCell score={call.closing_score} />
              </TableCell>
              <TableCell>
                <Typography variant="body2" sx={{
                  color: "text.secondary"
                }}>
                  {new Date(call.created_at).toLocaleDateString()}
                </Typography>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
